import { useEffect } from 'react';
import { BroadcastFormData } from '../types/broadcast.types';
import { Check, Calendar, Send, Image, Video, Music, Type, Link, MousePointerClick, Shuffle, Sparkles, ChevronDown, TriangleAlert, Wrench, Layers } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { LANGUAGES } from '../../Connection/constants/config-options';
import { useQuery } from '@tanstack/react-query';
import { getAiKeys } from '@/services/api/ai-key';
import { Loader2, AlertCircle } from 'lucide-react';
import { detectLanguage } from '@/utils/detectLanguage';
import type { UtilityEsteira } from '@/services/api/utility-esteiras';

interface StepReviewProps {
    formData: BroadcastFormData;
    /** Esteira selecionada (quando o wizard usa o modo "Esteira salva" em vez de 1+1). */
    selectedEsteira?: UtilityEsteira | null;
    onChange?: (aiTranslate: boolean, targetLanguage: string, aiProvider: string, sourceLanguage?: string) => void;
}

const blockIcons: Record<string, typeof Type> = {
    text: Type,
    image: Image,
    video: Video,
    audio: Music,
};

const blockLabels: Record<string, string> = {
    text: 'Texto',
    image: 'Foto',
    video: 'Vídeo',
    audio: 'Áudio',
};

const PROVIDER_MAP = [
    { value: 'openai', label: 'OpenAI' },
    { value: 'claude', label: 'Claude' },
    { value: 'gemini', label: 'Gemini' },
    { value: 'grok', label: 'Grok' },
    { value: 'deepseek', label: 'DeepSeek' },
];

export default function StepReview({ formData, selectedEsteira, onChange }: StepReviewProps) {
    const isEsteira = formData.broadcastType === 'esteira' || (formData.broadcastType === 'utility' && formData.selectedEsteiraId != null);
    const isUtility = (formData.broadcastType === 'utility' && !!formData.utilityTemplateId) || isEsteira;

    const { data: aiKeys = [], isLoading: isLoadingKeys } = useQuery({
        queryKey: ['ai-keys'],
        queryFn: getAiKeys,
        staleTime: 1000 * 60 * 5,
    });

    const activeProviders = aiKeys
        .filter(key => key.is_active)
        .map(key => key.provider.toLowerCase());

    const availableOptions = PROVIDER_MAP.filter(p => activeProviders.includes(p.value));
    const hasProviders = availableOptions.length > 0;

    // Se o provedor selecionado não está mais disponível, avisa ou reseta
    const currentProviderValid = formData.aiProvider ? activeProviders.includes(formData.aiProvider.toLowerCase()) : false;

    // Auto-detecta idioma do conteúdo ao entrar no step
    useEffect(() => {
        if (!formData.aiTranslate) return;
        const allText = formData.blocks
            .flatMap(b => [b.message, b.title, b.subtitle, ...(b.buttons?.map(btn => btn.text) ?? [])])
            .filter(Boolean)
            .join(' ');
        const detected = detectLanguage(allText);
        if (detected && !formData.sourceLanguage) {
            onChange?.(!!formData.aiTranslate, formData.targetLanguage || 'Inglês - Estados Unidos', formData.aiProvider || 'openai', detected);
        }
    }, []);

    // Warning: idioma detectado ≠ source selecionado
    const detectedLanguage = (() => {
        const allText = formData.blocks
            .flatMap(b => [b.message, b.title, b.subtitle, ...(b.buttons?.map(btn => btn.text) ?? [])])
            .filter(Boolean)
            .join(' ');
        return detectLanguage(allText);
    })();
    const showMismatchWarning =
        formData.aiTranslate &&
        formData.sourceLanguage &&
        detectedLanguage &&
        detectedLanguage !== formData.sourceLanguage;
    const showSameLanguageWarning =
        formData.aiTranslate &&
        formData.sourceLanguage &&
        formData.targetLanguage &&
        formData.sourceLanguage === formData.targetLanguage;

    return (
        <div className="space-y-6">
            <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Revisão Final</h3>
                <p className="text-sm text-gray-600 mt-2">
                    Confira todos os detalhes antes de enviar
                </p>
            </div>

            {/* ── Warnings de idioma (só modo normal) ── */}
            {!isUtility && showMismatchWarning && (
                <div className="flex items-start gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                    <TriangleAlert className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-500" />
                    <span>
                        Detectamos que o conteúdo está em <strong>{detectedLanguage}</strong>, mas você selecionou <strong>{formData.sourceLanguage}</strong> como idioma de origem. A tradução pode sair incorreta.
                    </span>
                </div>
            )}
            {!isUtility && showSameLanguageWarning && !showMismatchWarning && (
                <div className="flex items-start gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                    <TriangleAlert className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-500" />
                    <span>
                        O idioma de origem e destino são iguais (<strong>{formData.sourceLanguage}</strong>). A IA pode não traduzir corretamente. Verifique a seleção.
                    </span>
                </div>
            )}

            {/* ── AI Translation Global Config (só modo normal) ── */}
            {!isUtility && <div className="flex justify-center">
                <div className="inline-flex items-center gap-4 px-4 py-2 bg-white border border-gray-200 rounded-full shadow-sm">
                    <div className="flex items-center gap-2">
                        <div
                            className="flex items-center gap-2"
                            title={!hasProviders && !isLoadingKeys ? "Nenhuma chave de I.A configurada ou ativa." : undefined}
                        >
                            <Switch
                                id="global-ai-translate"
                                checked={!!formData.aiTranslate && hasProviders}
                                disabled={!hasProviders || isLoadingKeys}
                                onCheckedChange={(checked) => {
                                    if (checked && !formData.aiProvider && availableOptions.length > 0) {
                                        onChange?.(checked, formData.targetLanguage || 'Inglês - Estados Unidos', availableOptions[0].value, formData.sourceLanguage);
                                    } else {
                                        onChange?.(checked, formData.targetLanguage || 'Inglês - Estados Unidos', formData.aiProvider || 'openai', formData.sourceLanguage);
                                    }
                                }}
                                className="data-[state=checked]:bg-[#192335]"
                            />
                            <div className="flex items-center gap-1.5 ml-1">
                                <Sparkles className={`w-4 h-4 ${formData.aiTranslate && hasProviders ? 'text-blue-500 fill-blue-50' : 'text-gray-400'}`} />
                                <label
                                    htmlFor="global-ai-translate"
                                    className={`text-sm font-medium cursor-pointer ${!hasProviders ? 'text-gray-400' : 'text-gray-700'}`}
                                >
                                    Traduzir com I.A
                                </label>
                                {!hasProviders && !isLoadingKeys && (
                                    <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="w-px h-8 bg-gray-100" />

                    {/* Source Language */}
                    <div className="flex items-center gap-1.5">
                        <span className="text-xs text-gray-400 whitespace-nowrap">De:</span>
                        <SearchableSelect
                            value={formData.sourceLanguage || ''}
                            onValueChange={(v) => onChange?.(!!formData.aiTranslate, formData.targetLanguage || 'Inglês - Estados Unidos', formData.aiProvider || 'openai', v || undefined)}
                            options={[{ value: '', label: 'Auto' }, ...LANGUAGES]}
                            searchPlaceholder="Buscar idioma..."
                            className="border-0 shadow-none bg-transparent hover:bg-gray-100 h-8 text-sm font-medium w-auto min-w-[140px] rounded-lg"
                        />
                    </div>

                    <div className="w-px h-8 bg-gray-100" />

                    {/* Target Language */}
                    <SearchableSelect
                        value={formData.targetLanguage || 'Inglês - Estados Unidos'}
                        onValueChange={(v) => onChange?.(!!formData.aiTranslate, v, formData.aiProvider || 'openai', formData.sourceLanguage)}
                        options={LANGUAGES}
                        searchPlaceholder="Buscar idioma..."
                        className="border-0 shadow-none bg-transparent hover:bg-gray-100 h-8 text-sm font-medium w-auto min-w-[140px] rounded-lg"
                    />

                    <div className="w-px h-8 bg-gray-100" />

                    <div className="relative group min-w-[100px]">
                        {isLoadingKeys ? (
                            <div className="flex items-center justify-center px-4">
                                <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                            </div>
                        ) : (
                            <>
                                <select
                                    value={currentProviderValid ? formData.aiProvider : (availableOptions[0]?.value || '')}
                                    disabled={!hasProviders}
                                    onChange={(e) => onChange?.(!!formData.aiTranslate, formData.targetLanguage || 'Inglês - Estados Unidos', e.target.value, formData.sourceLanguage)}
                                    className={`appearance-none bg-transparent pl-2 pr-8 py-1 text-sm font-medium focus:outline-none cursor-pointer ${!hasProviders ? 'text-gray-400' : 'text-gray-900'}`}
                                >
                                    {availableOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                    {!hasProviders && <option value="">Nenhum</option>}
                                </select>
                                <ChevronDown className="absolute right-1 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                            </>
                        )}
                    </div>
                </div>
            </div>}

            <div className="max-w-2xl mx-auto space-y-4 max-h-[500px] overflow-y-auto pr-2">
                {/* Esteira salva: resumo de nome, páginas e templates da resolução persistida */}
                {isEsteira && selectedEsteira && (
                    <div className="bg-white border-2 border-blue-200 rounded-lg p-4 space-y-3">
                        <div className="flex items-center gap-2">
                            <Layers className="w-4 h-4 text-[#0f172a]" />
                            <p className="text-sm font-semibold text-gray-900">{selectedEsteira.name}</p>
                        </div>
                        <p className="text-xs text-gray-500 font-medium">
                            {selectedEsteira.template_resolution.length} template(s) · {selectedEsteira.page_ids.length} página(s) alvo
                        </p>
                        <div className="flex items-center gap-4 pt-2 border-t border-gray-100 text-xs">
                            <span className="text-emerald-600 font-medium">
                                {selectedEsteira.template_resolution.reduce((sum, r) => sum + r.page_ids.length, 0)} página(s) cobertas pela resolução salva
                            </span>
                        </div>
                    </div>
                )}

                {/* Template Utility (só modo 1+1) */}
                {isUtility && !isEsteira && formData.utilityTemplateData && (
                    <div className="bg-white border-2 border-blue-200 rounded-lg overflow-hidden">
                        {formData.utilityTemplateData.image_url ? (
                            <img
                                src={formData.utilityTemplateData.image_url}
                                alt="Header"
                                className="w-full h-32 object-cover"
                            />
                        ) : (
                            <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-4 py-2 flex items-center gap-2">
                                <Wrench className="w-4 h-4 text-white/80" />
                                <span className="text-xs text-white/90 font-semibold uppercase tracking-wider">Utility Template</span>
                            </div>
                        )}
                        <div className="p-4">
                            {formData.utilityTemplateData.title && (
                                <p className="text-sm font-semibold text-gray-900">{formData.utilityTemplateData.title}</p>
                            )}
                            {formData.utilityTemplateData.subtitle && (
                                <p className="text-xs text-gray-500 mt-0.5">{formData.utilityTemplateData.subtitle}</p>
                            )}
                            <p className="text-xs text-gray-400 mt-2 font-mono">{formData.utilityTemplateData.template_name}</p>
                        </div>
                    </div>
                )}

                {/* Páginas Selecionadas (modo normal/1+1) */}
                {!isEsteira && formData.selectedPages.length > 0 && (
                    <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
                        <p className="text-xs text-gray-500 font-medium mb-3">
                            Páginas Selecionadas ({formData.selectedPages.length})
                        </p>
                        <div className="space-y-2">
                            {formData.selectedPages.map((page) => (
                                <div key={page.page_id} className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-[#0f172a] rounded-full flex items-center justify-center flex-shrink-0">
                                        <span className="text-white font-semibold text-xs">
                                            {page.page_name.substring(0, 2).toUpperCase()}
                                        </span>
                                    </div>
                                    <p className="text-sm font-medium text-gray-900">{page.page_name}</p>
                                </div>
                            ))}
                        </div>
                        {formData.selectedPages.length > 1 && (
                            <p className="text-xs text-amber-700 bg-amber-50 rounded-md px-3 py-2 mt-3">
                                Será criado 1 broadcast por página ({formData.selectedPages.length} broadcasts no total)
                            </p>
                        )}
                    </div>
                )}

                {/* Páginas alvo (Esteira salva) */}
                {isEsteira && selectedEsteira && selectedEsteira.page_ids.length > 0 && (
                    <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
                        <p className="text-xs text-gray-500 font-medium mb-3">
                            Páginas alvo ({selectedEsteira.page_ids.length})
                        </p>
                        <p className="text-xs text-amber-700 bg-amber-50 rounded-md px-3 py-2">
                            Páginas sem cobertura de nenhum template da Esteira serão excluídas automaticamente.
                        </p>
                    </div>
                )}

                {/* Blocos de Conteúdo (só modo normal) */}
                {!isUtility && formData.blocks.length > 0 && (
                    <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
                        <p className="text-xs text-gray-500 font-medium mb-3">
                            Conteúdo ({formData.blocks.length} bloco{formData.blocks.length > 1 ? 's' : ''})
                        </p>
                        <div className="space-y-2">
                            {formData.blocks.map((block, index) => {
                                const Icon = blockIcons[block.type] || Type;
                                const label = blockLabels[block.type] || block.type;
                                const buttonCount = block.buttons?.length || 0;

                                return (
                                    <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                        <div className="flex-shrink-0 mt-0.5">
                                            <Icon className="w-4 h-4 text-gray-500" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs text-gray-400 font-medium">Bloco {index + 1}: {label}</p>
                                            {block.type === 'text' && block.message && (
                                                <p className="text-sm text-gray-900 mt-1 line-clamp-2">{block.message}</p>
                                            )}
                                            {block.type === 'image' && block.media_url && (
                                                <img src={block.media_url} alt="" className="w-24 h-16 object-cover rounded mt-1" />
                                            )}
                                            {(block.type === 'video' || block.type === 'audio') && block.media_filename && (
                                                <p className="text-sm text-gray-600 mt-1">{block.media_filename}</p>
                                            )}
                                            {buttonCount > 0 && (
                                                <div className="flex items-center gap-1 flex-wrap mt-1.5">
                                                    {block.buttons!.map((btn, btnIdx) => (
                                                        <span key={btnIdx} className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                                                            {btn.type === 'url' ? <Link className="w-3 h-3" /> : <MousePointerClick className="w-3 h-3" />}
                                                            {btn.text || 'Botão'}
                                                            {btn.type === 'url' && btn.url_entries && btn.url_entries.length > 0 && (
                                                                <span className="flex items-center gap-0.5 text-[10px] bg-blue-200 text-blue-800 px-1 rounded-full font-bold">
                                                                    <Shuffle className="w-2 h-2" />
                                                                    {btn.url_entries.length} URL{btn.url_entries.length > 1 ? 's' : ''}
                                                                </span>
                                                            )}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Agendamento */}
                <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
                    <p className="text-xs text-gray-500 font-medium mb-2">Agendamento</p>
                    {formData.isScheduled && formData.scheduledTime ? (
                        <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-cyan-600" />
                            <div>
                                <p className="text-sm font-semibold text-gray-900">Agendado</p>
                                <p className="text-xs text-gray-600">
                                    {new Date(formData.scheduledTime).toLocaleString('pt-BR', {
                                        dateStyle: 'full',
                                        timeStyle: 'short'
                                    })}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2">
                            <Send className="h-4 w-4 text-green-600" />
                            <p className="text-sm font-semibold text-gray-900">Envio Imediato</p>
                        </div>
                    )}
                </div>

                {/* Aviso Final */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-900">
                        <strong>Atenção:</strong> Após confirmar, {isEsteira
                            ? `os broadcasts serão ${formData.isScheduled ? 'agendados' : 'enviados imediatamente'}, cada página recebendo exatamente 1 template conforme a resolução salva na Esteira.`
                            : formData.selectedPages.length > 1
                                ? `${formData.selectedPages.length} broadcasts serão ${formData.isScheduled ? 'agendados' : 'enviados imediatamente'}, um para cada página selecionada.`
                                : `${formData.isScheduled ? 'seu broadcast será agendado' : 'seu broadcast será enviado imediatamente'} para todos os contatos da página selecionada.`
                        }{!isUtility && ` Cada lead receberá ${formData.blocks.length} mensage${formData.blocks.length > 1 ? 'ns' : 'm'} em sequência.`}
                    </p>
                </div>
            </div>
        </div>
    );
}
